import React, {
    createContext,
    useContext,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {Box} from '@mui/material';
import {WpNotesControl, type WpNotesControlProps} from '@/pages/components/audit/adjustment/adjust/support/WpNotesControl';

export type WpNotesData = Pick<
    WpNotesControlProps,
    'companyId' | 'annualId' | 'fsId' | 'initialNotes' | 'readOnly'
>;

type SlotApi = {
    tryClaim: (owner: symbol) => boolean;
    release: (owner: symbol) => void;
};

const WpNotesDataContext = createContext<WpNotesData | null>(null);
const WpNotesSlotContext = createContext<SlotApi | null>(null);

export function WpNotesProvider({
    children,
    companyId,
    annualId,
    fsId,
    initialNotes,
    readOnly,
}: WpNotesData & {children: React.ReactNode}) {
    const ownerRef = useRef<symbol | null>(null);

    const tryClaim = useMemo(() => {
        return (owner: symbol) => {
            if (ownerRef.current == null || ownerRef.current === owner) {
                ownerRef.current = owner;
                return true;
            }
            return false;
        };
    }, []);

    const release = useMemo(() => {
        return (owner: symbol) => {
            if (ownerRef.current === owner) {
                ownerRef.current = null;
            }
        };
    }, []);

    const data = useMemo(
        () => ({companyId, annualId, fsId, initialNotes, readOnly}),
        [companyId, annualId, fsId, initialNotes, readOnly],
    );

    const slot = useMemo(() => ({tryClaim, release}), [tryClaim, release]);

    return (
        <WpNotesDataContext.Provider value={data}>
            <WpNotesSlotContext.Provider value={slot}>
                {children}
            </WpNotesSlotContext.Provider>
        </WpNotesDataContext.Provider>
    );
}

export type WpNotesAnchorProps = Pick<WpNotesControlProps, 'trailing' | 'topRight'>;

/**
 * Renders at most one Notes control under the current WpNotesProvider.
 * First mounted anchor (e.g. button-row) wins; later table anchors stay silent.
 */
export function WpNotesAnchor({trailing, topRight}: WpNotesAnchorProps) {
    const data = useContext(WpNotesDataContext);
    const slot = useContext(WpNotesSlotContext);
    const ownerRef = useRef(Symbol('wp-notes-anchor'));
    const [active, setActive] = useState(false);

    useLayoutEffect(() => {
        if (!data) {
            setActive(false);
            return;
        }
        if (!slot) {
            setActive(true);
            return;
        }
        const ok = slot.tryClaim(ownerRef.current);
        setActive(ok);
        return () => {
            slot.release(ownerRef.current);
            setActive(false);
        };
    }, [data, slot]);

    if (!data) {
        // No Notes provider: still render topRight/trailing so WP toggles stay visible.
        if (!topRight && !trailing) return null;
        return (
            <Box sx={{mb: 1, width: '100%', minWidth: 0}}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box sx={{minWidth: 0}}>{trailing}</Box>
                    {topRight}
                </Box>
            </Box>
        );
    }

    if (!active) return null;

    return (
        <Box sx={{mb: 1, width: '100%', minWidth: 0}}>
            <WpNotesControl
                {...data}
                trailing={trailing}
                topRight={topRight}
            />
        </Box>
    );
}
